// Third Party Imports
import {createSelector} from 'reselect';
import get from 'lodash/get';

// Relative Imports
import {getTranslations, getListingExtended} from './store';
import {getIsMobile, getLocale} from './system';
import {getSearchUrl, getSearchBaseUrl} from './searchUrl';
import {
    getListingByOwnerProperties,
    getListingRecommendedProperties,
    getRecommendedPropertiesAlternativeTitle,
    getListingInquiryProperties,
    getClientRequestId
} from './listing';
import {getOwnerDetails} from './ownerDetails';

const hasHits = (hits) => (hits ? hits && hits.length : false);

// REST and GraphQl
const getRecommendedLoadingState = createSelector(
    [getListingExtended, getListingRecommendedProperties],
    (listingExtended, listingRecommendedProperties) => {
        const {
            recommendedPropertiesFetchFailed: fetchFailed,
            recommendedPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;

        const hits = hasHits(listingRecommendedProperties);

        return {
            isLoading: !fetchFailed && !fetchSuccess,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hits)
        };
    }
);

const getRecommendedPropertiesThumbnails = createSelector(
    [getIsMobile, getListingRecommendedProperties],
    (isMobile, recommendedProperties = []) => {
        const numHits = recommendedProperties.length;
        const targetLength = 5;
        let thumbnails = [];

        if (numHits) {
            // ensure there are 5 images, repeat thumbnails if needed
            thumbnails = [...new Array(targetLength).keys()].map((val, i) => {
                const hit = recommendedProperties[i]
                    ? recommendedProperties[i]
                    : recommendedProperties[i % numHits];

                return get(hit, 'listing.headlinePhoto.uri');
            });
        }

        return thumbnails;
    }
);

const getRecommendedProperties = createSelector(
    [
        getTranslations,
        getRecommendedLoadingState,
        getListingRecommendedProperties,
        getRecommendedPropertiesAlternativeTitle,
        getRecommendedPropertiesThumbnails,
        getClientRequestId,
        getLocale,
        getSearchUrl,
        getSearchBaseUrl
    ],
    (
        translations,
        loadingState,
        recommendedProperties,
        recommendedPropertiesAlternativeTitle,
        recommendedPropertiesThumbnails,
        clientRequestId,
        locale,
        searchUrl,
        searchUrlBase
    ) => {
        return {
            ...loadingState,
            clientRequestId,
            recommendedListings: recommendedProperties,
            recommendedPropertiesThumbnails,
            translations: {
                sleeps: translations.recoSleeps,
                bedroomsAbbreviation: translations.recoBedrooms,
                bathroomsAbbreviation: translations.recoBathrooms,
                seoAveragePrice: translations.recoAveragePrice,
                seoTotalPrice: translations.recoTotalPrice,
                seeMore: translations.recoSeeMore,
                seeMoreProperties: translations.recoSeeMoreProperties,
                viewMore: translations.recoViewMore
            },
            searchUrl,
            searchUrlBase,
            locale,
            title: recommendedPropertiesAlternativeTitle || translations.recommendationMessage
        };
    }
);

const getByOwnerLoadingState = createSelector(
    [getListingExtended, getListingByOwnerProperties],
    (listingExtended, listingByOwnerProperties) => {
        const {
            byOwnerPropertiesFetchFailed: fetchFailed,
            byOwnerPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;

        const hits = hasHits(listingByOwnerProperties);

        return {
            isLoading: !fetchFailed && !fetchSuccess,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hits)
        };
    }
);

const getByOwnerProperties = createSelector(
    [
        getTranslations,
        getByOwnerLoadingState,
        getListingByOwnerProperties,
        getClientRequestId,
        getLocale,
        getOwnerDetails
    ],
    (
        translations,
        loadingState,
        listingByOwnerProperties,
        locale,
        ownerDetails,
        clientRequestId
    ) => {
        return {
            ...loadingState,
            clientRequestId,
            recommendedListings: listingByOwnerProperties,
            translations: {
                byOwnerMessage: translations.recoByOwnerMessage,
                sleeps: translations.recoSleeps,
                bedroomsAbbreviation: translations.recoBedrooms,
                bathroomsAbbreviation: translations.recoBathrooms,
                seoAveragePrice: translations.recoAveragePrice,
                seoTotalPrice: translations.recoTotalPrice
            },
            ownerDetails,
            locale
        };
    }
);

const getInquiryLoadingState = createSelector(
    [getListingExtended, getListingInquiryProperties],
    (listingExtended, listingInquiryProperties) => {
        const {
            inquiryPropertiesFetchFailed: fetchFailed,
            inquiryPropertiesFetchSuccess: fetchSuccess
        } = listingExtended;

        const hits = hasHits(listingInquiryProperties);

        return {
            isLoading: !fetchFailed && !fetchSuccess,
            isEmptyOrFailed: fetchFailed || (fetchSuccess && !hits)
        };
    }
);

const getInquiryProperties = createSelector(
    [
        getTranslations,
        getInquiryLoadingState,
        getListingInquiryProperties,
        getClientRequestId,
        getLocale,
        getSearchUrl,
        getSearchBaseUrl
    ],
    (
        isMobile,
        translations,
        loadingState,
        listingInquiryProperties,
        clientRequestId,
        locale,
        searchUrl,
        searchUrlBase
    ) => {
        return {
            ...loadingState,
            clientRequestId,
            recommendedListings: listingInquiryProperties,
            translations: {
                sleeps: translations.recoSleeps,
                bedroomsAbbreviation: translations.recoBedrooms,
                bathroomsAbbreviation: translations.recoBathrooms,
                seoAveragePrice: translations.recoAveragePrice,
                seoTotalPrice: translations.recoTotalPrice,
                seeMore: translations.recoSeeMore,
                seeMoreProperties: translations.recoSeeMoreProperties,
                viewMore: translations.recoViewMore
            },
            searchUrl,
            searchUrlBase,
            locale,
            title: translations.recommendationMessage
        };
    }
);

export {
    getRecommendedProperties,
    getByOwnerProperties,
    getInquiryProperties,
    getRecommendedLoadingState,
    getRecommendedPropertiesThumbnails,
    getByOwnerLoadingState,
    getInquiryLoadingState,
    hasHits
};
